import { FormControl, FormGroup } from '@angular/forms';
import { resolveShopPluginCdn } from '@app/pages/checkout-app/utils';

export type TimerUnit = 'minutes' | 'hours';
export type TimerFormat = 'digital' | 'compact';
export type TimerPlacement = 'inline' | 'badge' | 'in_goal_message' | 'in_title';
export type TimerExpirationBehavior = 'show_expired_message' | 'hide_progress_bar';
export type TimerPreviewState = 'default' | 'low' | 'expired';

export interface TimerMessageStyleFormShape {
  message: FormControl<string>;
  backgroundColor: FormControl<string>;
  textColor: FormControl<string>;
  iconUrl: FormControl<string | File>;
  customIconUrl: FormControl<string | File>;
}

export interface TimerLowFormShape extends TimerMessageStyleFormShape {
  threshold: FormGroup<{
    value: FormControl<number>;
    unit: FormControl<TimerUnit>;
  }>;
}

export interface TimerExpirationFormShape extends TimerMessageStyleFormShape {
  behavior: FormControl<TimerExpirationBehavior>;
}

export interface TimerFormShape {
  enabled: FormControl<boolean>;
  duration: FormGroup<{
    value: FormControl<number>;
    unit: FormControl<TimerUnit>;
  }>;
  format: FormControl<TimerFormat>;
  placement: FormControl<TimerPlacement>;
  defaultMessage: FormGroup<TimerMessageStyleFormShape>;
  lowTimer: FormGroup<TimerLowFormShape>;
  expiration: FormGroup<TimerExpirationFormShape>;
}

const timerIconCdn = resolveShopPluginCdn();
export const TIMER_PRESET_ICON_NAMES = ['clock', 'lightning', 'fire', 'hourglass', 'target'] as const;
export const TIMER_PRESET_ICON_URLS: string[] = TIMER_PRESET_ICON_NAMES.map(
  (name) => `${timerIconCdn}/icons/${name}-color.svg`,
);

export const TIMER_DEFAULT_MESSAGE = 'Only {countdown} left to claim this offer!';
export const TIMER_LOW_MESSAGE = 'Hurry - only {countdown} left!';
export const TIMER_EXPIRED_MESSAGE = 'Offer expired';

export const TIMER_DEFAULT_BACKGROUND = '#F5F5F5';
export const TIMER_LOW_BACKGROUND = '#FFF5EB';
export const TIMER_EXPIRED_BACKGROUND = '#FEF6F6';
export const TIMER_MESSAGE_TEXT_COLOR = '#000000';

export function getSampleTimerCountdown(state: TimerPreviewState, format?: TimerFormat): string {
  const compact = format === 'compact';
  switch (state) {
    case 'expired':
      return compact ? '0s' : '00:00';
    case 'low':
      return compact ? '30s' : '00:30';
    default:
      return compact ? '4m 59s' : '04:59';
  }
}

export function getTimerPreviewStyle(
  state?: { backgroundColor?: string; textColor?: string } | null,
): Record<string, string> {
  return {
    'background-color': state?.backgroundColor ?? TIMER_DEFAULT_BACKGROUND,
    color: state?.textColor ?? TIMER_MESSAGE_TEXT_COLOR,
  };
}

export function getActiveTimerPreviewState<T>(
  timer: { defaultMessage: T; lowTimer: T; expiration: T } | undefined | null,
  state: TimerPreviewState,
): T | undefined {
  if (!timer) return undefined;
  switch (state) {
    case 'low':
      return timer.lowTimer;
    case 'expired':
      return timer.expiration;
    default:
      return timer.defaultMessage;
  }
}

function buildTimerMessageStyleControls(
  message: string,
  backgroundColor: string,
  textColor: string,
): TimerMessageStyleFormShape {
  return {
    message: new FormControl(message, { nonNullable: true }),
    backgroundColor: new FormControl(backgroundColor, { nonNullable: true }),
    textColor: new FormControl(textColor, { nonNullable: true }),
    iconUrl: new FormControl<string | File>('', { nonNullable: true }),
    customIconUrl: new FormControl<string | File>('', { nonNullable: true }),
  };
}

export function createTimerFormGroup(initial?: Record<string, unknown> | null): FormGroup<TimerFormShape> {
  const group = new FormGroup<TimerFormShape>({
    enabled: new FormControl(false, { nonNullable: true }),
    duration: new FormGroup({
      value: new FormControl(5, { nonNullable: true }),
      unit: new FormControl<TimerUnit>('minutes', { nonNullable: true }),
    }),
    format: new FormControl<TimerFormat>('digital', { nonNullable: true }),
    placement: new FormControl<TimerPlacement>('inline', { nonNullable: true }),
    defaultMessage: new FormGroup<TimerMessageStyleFormShape>({
      ...buildTimerMessageStyleControls(TIMER_DEFAULT_MESSAGE, TIMER_DEFAULT_BACKGROUND, TIMER_MESSAGE_TEXT_COLOR),
    }),
    lowTimer: new FormGroup<TimerLowFormShape>({
      ...buildTimerMessageStyleControls(TIMER_LOW_MESSAGE, TIMER_LOW_BACKGROUND, TIMER_MESSAGE_TEXT_COLOR),
      threshold: new FormGroup({
        value: new FormControl(1, { nonNullable: true }),
        unit: new FormControl<TimerUnit>('minutes', { nonNullable: true }),
      }),
    }),
    expiration: new FormGroup<TimerExpirationFormShape>({
      ...buildTimerMessageStyleControls(TIMER_EXPIRED_MESSAGE, TIMER_EXPIRED_BACKGROUND, TIMER_MESSAGE_TEXT_COLOR),
      behavior: new FormControl<TimerExpirationBehavior>('show_expired_message', { nonNullable: true }),
    }),
  });

  if (initial) {
    group.patchValue(initial, { emitEvent: false });
  }
  return group;
}
